/*
* Texas Disclosure Page
*/

import React, { useEffect, memo, lazy, Suspense, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { Row, Col, Container, Spinner } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import styles from './index.module.css'

const key = 'Texas_Disclosure';
const FooterDisclaimer = lazy(() => import("../../components/FooterDisclaimer/FooterDisclaimer"));

export function TexasDisclosure({
    brokerData, brokerUrl
}) {
    const location = useLocation();

    // Function to extract username from URL
    const getUserNameFromURL = () => {
        try {
            const pathSegments = window.location.pathname.split('/').filter(segment => segment);
            // Assuming URL structure is /partner/{username}/texas-disclosure/
            const partnerIndex = pathSegments.indexOf('partner');
            if (partnerIndex !== -1 && pathSegments[partnerIndex + 1]) {
                return pathSegments[partnerIndex + 1];
            }
            return null;
        } catch (error) {
            console.error('Error extracting username from URL:', error);
            return null;
        }
    };

    // Create canonical URL using useMemo for performance
    const canonicalUrl = useMemo(() => {
        try {
            const userName = getUserNameFromURL();

            if (!userName) {
                console.error('Invalid username in URL');
                return null;
            }

            // Clean and validate the username
            const cleanUserName = encodeURIComponent(userName.trim());

            // Construct the canonical URL for texas-disclosure
            const protocol = 'https://';
            const domain = 'www.angelai.com';
            const basePath = '/partner/';
            const disclosurePath = '/texas-disclosure/';
            const canonicalUrl = `${protocol}${domain}${basePath}${cleanUserName}${disclosurePath}`;

            // Validate the constructed URL
            try {
                new URL(canonicalUrl);
                console.log('Created canonical URL for Texas Disclosure:', canonicalUrl);
                return canonicalUrl;
            } catch (e) {
                console.error('Invalid URL constructed:', canonicalUrl);
                return null;
            }
        } catch (error) {
            console.error('Error creating canonical URL:', error);
            return null;
        }
    }, [location.pathname]);

    return (
        <article>
            <Helmet>
                <title>
                    {brokerData?.name ? `${brokerData.name} | Sun West Mortgage Company, Inc. - Texas Complaint Notice and Servicing Disclosure` : "Find Your Dream Home with a Trusted Mortgage Partner"}
                </title>
                <meta
                    name="description"
                    content={brokerData?.name ? `${brokerData.name} | Sun West Mortgage Company, Inc. - Texas Complaint Notice and Servicing Disclosure` : "Find Your Dream Home with a Trusted Mortgage Partner"}
                />
                {/* Canonical URL */}
                {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

                <meta property="og:url" content={canonicalUrl || window.location.href} />
                <meta property="og:title" content={brokerData?.name ? `${brokerData.name} | Sun West Mortgage Company, Inc. - Texas Complaint Notice and Servicing Disclosure` : "Find Your Dream Home with a Trusted Mortgage Partner"} />
                <meta property="og:description" content="Purchase, refinancing, property rehabilitation loan, and reverse mortgage are among the residential house loan mortgages offered by Sun West Mortgage Company, Inc." />
                <meta property="og:keywords" content="swmc texas disclosure, Broker, Sun West, A.I., Chat, Bot, AngelAi, Assistant, Empathetic Technology, 24/7, Mortgage, Loan, Underwriter, Loan Assistant, Homebuying, Homebuyer, Home Loan, Real Estate, Real Estate Deals, Loan Originator, Loan Officer, Lender, Divorce Your Processor, Loan Keeping™, Instant Underwriting™, RealtorⓇ, Mortgage Technology, Blockchain Technology, TRU ApprovalⓇ, The Processor, Low Rates, Mortgage Possible, Homebuyer Connect, Wholesale, Correspondent, Faster Conditions, Quick Turn Times, AngelAi, Break Free, Underwriter in Your Pocket, Pocket Underwriter, Underwriter at Your Fingertips, useAngelAi.com, Quick Underwriting, Ask AngelAi" />
                <meta name="twitter:url" content={canonicalUrl || window.location.href} />
                <meta name="twitter:title" content={brokerData?.name ? `${brokerData.name} | Sun West Mortgage Company, Inc. - Texas Complaint Notice and Servicing Disclosure` : "Find Your Dream Home with a Trusted Mortgage Partner"} />
                <meta name="twitter:description" content="Purchase, refinancing, property rehabilitation loan, and reverse mortgage are among the residential house loan mortgages offered by Sun West Mortgage Company, Inc." />
            </Helmet>
            <div>

                <div className={styles.hero_image_txdis}>
                    <div>
                        <h1>Texas Disclosure</h1>
                    </div>
                </div>
                <Container className={styles.pageContainer_txdis}>
                    <Row>
                        <Col md={12}>
                            <p className={styles.heading_txdis}><strong>Texas Complaint Notice:</strong></p>
                            <p>Figure: 7 TAC §81.200(c)</p>
                            <h4><u>TEXAS MORTGAGE BANKER DISCLOSURE</u></h4>
                            <p>"CONSUMERS WISHING TO FILE A COMPLAINT AGAINST A MORTGAGE BANKER OR A LICENSED MORTGAGE BANKER
                                RESIDENTIAL MORTGAGE LOAN ORIGINATOR SHOULD COMPLETE AND SEND A COMPLAINT FORM TO THE TEXAS
                                DEPARTMENT OF SAVINGS AND MORTGAGE LENDING, 2601 NORTH LAMAR, SUITE 201, AUSTIN, TEXAS 78705.
                                COMPLAINT FORMS AND INSTRUCTIONS MAY BE OBTAINED FROM THE DEPARTMENT'S WEBSITE AT <a href="http://www.sml.texas.gov/" target="_blank" title="Texas Department of Savings and Mortgage Lending"> WWW.SML.TEXAS.GOV</a>.
                                A TOLL-FREE CONSUMER HOTLINE IS AVAILABLE AT <a href="tel:+18772765550">1-877-276-5550</a>.</p>
                            <p>THE DEPARTMENT MAINTAINS A RECOVERY FUND TO MAKE PAYMENTS OF CERTAIN ACTUAL OUT OF POCKET DAMAGES
                                SUSTAINED BY BORROWERS CAUSED BY ACTS OF LICENSED MORTGAGE BANKER RESIDENTIAL MORTGAGE LOAN
                                ORIGINATORS. A WRITTEN APPLICATION FOR REIMBURSEMENT FROM THE RECOVERY FUND MUST BE FILED WITH AND
                                INVESTIGATED BY THE DEPARTMENT PRIOR TO THE PAYMENT OF A CLAIM. FOR MORE INFORMATION ABOUT THE
                                RECOVERY FUND, PLEASE CONSULT THE DEPARTMENT'S WEBSITE AT<a href="http://www.sml.texas.gov/" target="_blank" title="Texas Department of Savings and Mortgage Lending"> WWW.SML.TEXAS.GOV</a>."</p>
                            <p>THIS DISCLOSURE NOTICE ONLY APPLIES TO RESIDENTIAL MORTGAGE LOANS ON REAL ESTATE LOCATED IN TEXAS.
                            </p>
                            <p><strong>Texas Servicing Disclosure:</strong></p>
                            <p>The following disclosure notice only applies to residential mortgage loans on real estate located in
                                Texas:</p>
                            <p><i>COMPLAINTS REGARDING THE SERVICING OF YOUR MORTGAGE SHOULD BE SENT TO THE DEPARTMENT OF SAVINGS
                                AND MORTGAGE LENDING, 2601 NORTH LAMAR, SUITE 201, AUSTIN, TX 78705. A TOLL-FREE CONSUMER
                                HOTLINE IS AVAILABLE AT <a href="tel:+18772765550">877-276-5550</a>.
                            </i></p>
                            <p className='mb-4'>A complaint form and instructions may be downloaded and printed from the Department's website located
                                at <a href="http://www.sml.texas.gov/" target="_blank" title="Texas Department of Savings and Mortgage Lending">www.sml.texas.gov</a> or obtained from the department upon request by mail at the address above, by
                                telephone at its toll-free consumer hotline listed above, or by email at <a href="mailto:smlinfo@sml.texas.gov" title="Texas Department of Savings and Mortgage Lending">smlinfo@sml.texas.gov</a>.</p>
                        </Col>
                    </Row>
                </Container>
            </div>
            <Suspense fallback={<div><Spinner /></div>}>
                <FooterDisclaimer brokerData={brokerData} brokerUrl={brokerUrl} />
            </Suspense>
        </article>
    );
}
export default TexasDisclosure